import { useEffect, useState } from 'react';
import { DateRangeState } from '../../../components/Dashboard/DateRangeControl/useDateRange';
import { severity, useNotification } from '../../../components/Notification';
import { useNavigate } from 'react-router';
import { AVG_ENDPOINT_MAP } from '../constants';
import { DashboardAvgData, KpiData, ShortKpiData } from '../types';
import { fetchData } from '../../../axios/fetchData';
import { useUserStore } from '../../../store';
import { isDevMode } from '../../../utils/isDevMode';
import { getDateRange } from '../../../components/Dashboard/DateRangeControl/helpers';

type DashboardConfigType = {
  dateRange: DateRangeState;
  salonClassification: string;
};

export const useDashboardAvgData = ({ dateRange, salonClassification }: DashboardConfigType) => {
  const { showMessage } = useNotification();
  const { user } = useUserStore();
  const navigate = useNavigate();

  const [avgData, setAvgData] = useState<DashboardAvgData>({});

  const defaultIsLoading: Record<string, boolean> = {};
  Object.keys(AVG_ENDPOINT_MAP).forEach((key) => {
    defaultIsLoading[key] = false;
  });
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>(defaultIsLoading);

  useEffect(() => {
    async function load() {
      const { dateString } = getDateRange({
        period: dateRange.name,
        year: dateRange.year,
        quarter: dateRange.quarter,
      });

      (Object.keys(AVG_ENDPOINT_MAP) as Array<keyof DashboardAvgData>).forEach(async (key) => {
        setIsLoading((prev) => {
          return { ...prev, [key]: true };
        });
        const endpoint = AVG_ENDPOINT_MAP[key];

        const url = isDevMode()
          ? `${import.meta.env.VITE_APP_SDP_DASHBOARD}${endpoint}.json`
          : `${import.meta.env.VITE_APP_SDP_DASHBOARD}avg_data/${user.id}/${salonClassification}/${endpoint}/cd/${dateString}`;

        try {
          const result = await fetchData(
            {
              url,
              params: {
                _format: 'json',
              },
            },
            (message: string, type: severity) => {
              if (message !== 'A Dashboard data not found.') showMessage(message, type);
            },
            navigate,
          );

          if (result) {
            setAvgData((prev) => ({
              ...prev,
              [key]: result.data as KpiData | ShortKpiData,
            }));
          }
        } catch (err) {
          console.error(`Error loading ${endpoint}`, err);
        }
        setIsLoading((prev) => {
          return { ...prev, [key]: false };
        });
      });
    }

    setAvgData({});

    if (user?.id && dateRange && salonClassification) {
      load();
    }
  }, [dateRange, salonClassification]);

  return {
    avgData,
    isLoading,
  };
};
